import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import PropertyCard from "@/components/PropertyCard";
import LeadForm from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ShieldCheck, Users, MapPin, Scale, Landmark, FileCheck, ArrowRight,
  Building2, Home as HomeIcon, LandPlot, Search, Star, Quote, CheckCircle2
} from "lucide-react";

const WHY = [
  { icon: ShieldCheck, title: "Verified Projects", desc: "We work only with RERA-registered projects from reputed developers." },
  { icon: Users, title: "Personalised Consultation", desc: "Advice tailored to your budget, location and lifestyle needs." },
  { icon: MapPin, title: "Site-Visit Assistance", desc: "We arrange and accompany you on organised project site visits." },
  { icon: Scale, title: "Price & Project Comparison", desc: "Compare multiple projects side-by-side on price and value." },
  { icon: Landmark, title: "Home-Loan Guidance", desc: "Connect with trusted lenders for the best home-loan options." },
  { icon: FileCheck, title: "Complete Booking Support", desc: "End-to-end help with documentation and the booking process." },
];

const STEPS = [
  { n: "01", title: "Tell Us Your Requirements", desc: "Share your budget, preferred location and property type." },
  { n: "02", title: "Receive Shortlisted Properties", desc: "We curate verified projects that match your needs." },
  { n: "03", title: "Visit & Compare Projects", desc: "Tour shortlisted projects and compare them side-by-side." },
  { n: "04", title: "Get Booking Assistance", desc: "We help you negotiate, finalise and complete the booking." },
];

const TYPES = [
  { icon: Building2, label: "Apartments", type: "Apartment" },
  { icon: HomeIcon, label: "Villas", type: "Villa" },
  { icon: LandPlot, label: "Plots", type: "Plot" },
];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function Home() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [newLaunch, setNewLaunch] = useState(null);
  const [locations, setLocations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [builders, setBuilders] = useState([]);

  useEffect(() => {
    api.get("/projects?featured=true&limit=6").then((r) => setFeatured(r.data)).catch(() => setFeatured([]));
    api.get("/projects?new_launch=true&limit=3").then((r) => setNewLaunch(r.data)).catch(() => setNewLaunch([]));
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/builders").then((r) => setBuilders(r.data)).catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "RealEstateAgent",
    name: settings?.brand_name || "Mathura Homes",
    description: settings?.seo?.home_description,
    telephone: settings?.phone, email: settings?.email,
    address: { "@type": "PostalAddress", addressLocality: "Bangalore", addressRegion: "Karnataka", addressCountry: "IN" },
    areaServed: "Bangalore",
  };

  return (
    <div>
      <SEO title={settings?.seo?.home_title || "Mathura Homes | Bangalore Real Estate"}
        description={settings?.seo?.home_description} jsonLd={jsonLd} image={featured?.[0]?.images?.[0]} />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center" data-testid="hero-section">
        <img src="https://images.pexels.com/photos/15223780/pexels-photo-15223780.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="Bangalore skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/75" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center w-full">
          <motion.div {...fadeUp}>
            <span className="inline-block h-1 w-16 bg-gold mb-6" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
              Your Trusted Partner for <span className="text-gold">Bangalore Real Estate</span>
            </h1>
            <p className="text-base md:text-lg text-gray-200 mt-6 max-w-xl leading-relaxed">
              Discover verified residential projects and receive personalised guidance—from property shortlisting and site visits to negotiation and booking.
            </p>
            <p className="text-sm text-gray-300 mt-4 max-w-xl">
              We help you compare the right projects, understand pricing and make informed property decisions with confidence.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Button onClick={() => navigate("/properties")} data-testid="hero-explore-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold px-7 py-6 text-base shadow-gold active:scale-95 transition-transform">
                Find Your Property <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => document.getElementById("quick-enquiry")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-consult-btn" variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-navy px-7 py-6 text-base">
                Talk to an Advisor
              </Button>
            </div>
          </motion.div>

          <motion.div id="quick-enquiry" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-card-hover p-6 md:p-8" data-testid="hero-enquiry-form">
            <h3 className="font-display text-2xl text-navy mb-1">Book a Free Consultation</h3>
            <p className="text-sm text-muted-foreground mb-5">Share your requirements and our advisor will call you back.</p>
            <LeadForm variant="quick" leadType="consultation" sourcePage="home-hero" />
          </motion.div>
        </div>
      </section>

      {/* SEARCH BY TYPE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {TYPES.map((t) => (
            <button key={t.type} onClick={() => navigate(`/properties?type=${encodeURIComponent(t.type)}`)} data-testid={`search-type-${t.type.toLowerCase()}`}
              className="group flex items-center gap-4 p-6 rounded-2xl bg-white border border-gray-100 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all">
              <span className="p-3 rounded-xl bg-navy/5 text-navy group-hover:bg-gold group-hover:text-navy transition-colors"><t.icon className="w-7 h-7" strokeWidth={1.5} /></span>
              <div className="text-left">
                <p className="font-display text-xl text-navy">{t.label}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1">Browse <Search className="w-3 h-3" /></p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <Section title="Featured Projects" subtitle="Handpicked premium projects across Bangalore" testid="featured-section">
        <Grid loading={featured === null} items={featured} render={(p) => <PropertyCard key={p.id} p={p} />} empty="No featured projects yet." />
        <div className="text-center mt-10">
          <Button asChild variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white"><Link to="/properties" data-testid="view-all-properties">View All Properties <ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
        </div>
      </Section>

      {/* WHY CHOOSE US */}
      <section className="bg-navy py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Why Mathura Homes</span>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-3">A Trusted Advisor by Your Side</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <motion.div key={w.title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="p-7 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-testid={`why-card-${i}`}>
                <span className="inline-flex p-3 rounded-xl bg-gold/15 text-gold mb-4"><w.icon className="w-6 h-6" strokeWidth={1.5} /></span>
                <h3 className="font-display text-xl text-white mb-2">{w.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <Section title="How It Works" subtitle="A simple, transparent process to your new home" testid="how-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="relative p-7 rounded-2xl bg-muted border border-gray-100">
              <span className="font-display text-5xl text-gold/40 font-bold">{s.n}</span>
              <h3 className="font-display text-lg text-navy mt-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* NEW LAUNCHES */}
      <section className="bg-muted py-24" data-testid="new-launch-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead title="Newly Launched Projects" subtitle="Be the first to explore Bangalore's latest launches" />
          <Grid loading={newLaunch === null} items={newLaunch} render={(p) => <PropertyCard key={p.id} p={p} />} empty="No new launches yet." cols="lg:grid-cols-3" />
        </div>
      </section>

      {/* POPULAR LOCATIONS */}
      <Section title="Popular Bangalore Locations" subtitle="Explore properties in Bangalore's most sought-after areas" testid="locations-section">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {locations.map((l) => (
            <Link key={l.id} to={`/properties-in-${l.slug}`} data-testid={`location-${l.slug}`}
              className="group relative rounded-2xl overflow-hidden aspect-[3/4] shadow-card hover:shadow-card-hover transition-all">
              <img src={l.hero_image} alt={l.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <h3 className="font-display text-lg text-white leading-tight">{l.name}</h3>
                <p className="text-xs text-gold flex items-center gap-1 mt-1">Explore <ArrowRight className="w-3 h-3" /></p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-navy py-24" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Testimonials</span>
              <h2 className="font-display text-3xl md:text-4xl text-white mt-3">What Our Clients Say</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white/5 border border-white/10 p-8 rounded-2xl relative" data-testid={`testimonial-${t.id}`}>
                  <Quote className="w-8 h-8 text-gold/40 mb-3" />
                  <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}</div>
                  <p className="text-gray-300 text-sm leading-relaxed">"{t.text}"</p>
                  <div className="mt-5"><p className="text-white font-semibold">{t.name}</p><p className="text-xs text-gold">{t.role}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BUILDERS */}
      {builders.length > 0 && (
        <Section title="Builder & Developer Partners" subtitle="We work with Bangalore's most reputed developers" testid="builders-section">
          <div className="flex flex-wrap justify-center gap-4">
            {builders.map((b) => (
              <div key={b.id} className="px-8 py-5 rounded-xl bg-muted border border-gray-100 font-display text-lg text-navy" data-testid={`builder-${b.id}`}>{b.name}</div>
            ))}
          </div>
        </Section>
      )}

      {/* FINAL CTA */}
      <section className="relative py-24" data-testid="final-cta">
        <img src="https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="Consultation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white">Ready to Find Your Perfect Property?</h2>
          <p className="text-gray-300 mt-4">Book a free consultation with our advisors and let us guide you every step of the way.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Button onClick={() => navigate("/contact")} className="bg-gold text-navy hover:bg-gold-dark font-semibold px-7 py-6">Book a Free Consultation</Button>
            <Button onClick={() => navigate("/properties")} variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-navy px-7 py-6">Explore Properties</Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHead({ title, subtitle }) {
  return (
    <div className="text-center max-w-2xl mx-auto mb-14">
      <span className="inline-block h-1 w-12 bg-gold mb-4" />
      <h2 className="font-display text-3xl md:text-4xl text-navy">{title}</h2>
      {subtitle && <p className="text-muted-foreground mt-3">{subtitle}</p>}
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid={testid}>
      <SectionHead title={title} subtitle={subtitle} />
      {children}
    </section>
  );
}

function Grid({ loading, items, render, empty, cols = "lg:grid-cols-3" }) {
  if (loading) return <div className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-8`}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}</div>;
  if (!items || items.length === 0) return <p className="text-center text-muted-foreground py-8">{empty}</p>;
  return <div className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-8`}>{items.map(render)}</div>;
}
